/**
 * Selection Manager - Handles product selection state and cart operations
 * Part of the SOLID Course Materials System
 *
 * @author Course Materials Team
 * @version 1.0.0
 */

class SelectionManager {
  /**
   * @param {Object} config - Configuration object
   * @param {Array<any>} config.products - Products array
   * @param {any} config.productHandler - SOLID product handler instance
   */
  constructor(config = {}) {
    this.products = config.products || [];
    this.productHandler = config.productHandler || null;
    this.selectedProducts = [];
    this._isSyncing = false; // Flag to prevent re-entrant sync calls

    // Bind methods to preserve context
    this.updateSelectedProducts = this.updateSelectedProducts.bind(this);
    this.updateSelectionBarSummary = this.updateSelectionBarSummary.bind(this);
    this.clearSelections = this.clearSelections.bind(this);
    this.getSelectedCount = this.getSelectedCount.bind(this);
    this.getTotalPrice = this.getTotalPrice.bind(this);
    this.findVariant = this.findVariant.bind(this);
    this.addSelectedProductsToCart = this.addSelectedProductsToCart.bind(this);
  }

  /**
   * Updates the internal selected products array based on checked checkboxes
   * Preserves items with source: 'suggested' that are not represented by checkboxes
   * @returns {void}
   */
  updateSelectedProducts() {
    // Preserve suggested materials items - only clear checkbox-based items
    const suggestedItems = this.selectedProducts.filter(item => item.source === 'suggested');
    this.selectedProducts = [...suggestedItems]; // Start with suggested items preserved
    const checkboxes = document.querySelectorAll('.purchase-checkbox:checked');
    checkboxes.forEach(
      /** @param {any} checkbox */ checkbox => {
        const materialItem = checkbox.closest('.material-item');
        const selectedFormat = this.getSelectedFormat(materialItem);
        const productId = checkbox.dataset.productId;
        const purchaseType = checkbox.dataset.purchaseType;
        const variantId = checkbox.dataset.variantId;
        const productTitle = checkbox.dataset.productTitle;
        const sectionId = checkbox.dataset.sectionId || '';
        // Check if this is a simple product (determined by html-renderer based on variant data)
        const formatBtns = materialItem.querySelectorAll('.format-btn');
        const isSimple = formatBtns.length === 0;

        // Simple products only need productId & purchaseType, complex products also need selectedFormat
        const hasRequiredData = isSimple
          ? productId && purchaseType
          : selectedFormat && productId && purchaseType;

        if (hasRequiredData) {
          const product = window.courseData.products_Materials.find(
            /** @param {any} p */ p => p.id == productId
          );

          // For simple products, use variant ID directly. For complex products, use format-based lookup
          const variant = isSimple
            ? product?.variants.find(
                /** @param {any} v */ v => v.id == variantId
              )
            : product?.variants.find(
                /** @param {any} v */ v => v.id == variantId
              ) || this.findVariant(productId, selectedFormat, purchaseType);

          if (variant) {
            // Deduplicate by (variantId, sectionId) - same product in different sections adds twice
            const extractedVariantId = this.extractVariantId(variant.id);
            const alreadyExists = this.selectedProducts.some(item => {
              const sameVariant = this.extractVariantId(item.variant?.id) === extractedVariantId;
              if (item.source === 'suggested') return sameVariant;
              return sameVariant && (item.sectionId || '') === sectionId;
            });

            if (!alreadyExists) {
              this.selectedProducts.push({
                productId,
                productTitle,
                selectedFormat: selectedFormat || 'simple',
                purchaseType,
                variant,
                price: parseFloat(variant.price),
                sectionId,
              });
            }
          }
        }
      }
    );

    // Dispatch custom event for external listeners
    this.dispatchSelectionChangeEvent();
  }

  /**
   * Updates the selection bar component with current totals
   * @returns {void}
   */
  updateSelectionBarSummary() {
    const selectionBar = document.querySelector('selection-bar-component');
    if (!selectionBar) return;

    const totalItems = this.selectedProducts.length;
    const totalPrice = this.selectedProducts.reduce(
      (sum, /** @param {any} item */ item) => sum + item.price,
      0
    );

    // Only update if values actually changed to prevent unnecessary updates
    const currentCount = selectionBar.currentSelectedCount;
    const currentTotal = parseFloat(selectionBar.currentSelectedTotal || '0');
    const newTotal = parseFloat(totalPrice.toFixed(2));

    if (currentCount !== totalItems || currentTotal !== newTotal) {
      // Update selection bar display
      selectionBar.currentSelectedCount = totalItems;
      selectionBar.currentSelectedTotal = totalPrice.toFixed(2);
      selectionBar.updateVisibility();
    }
  }

  /**
   * Gets the selected format from a material item
   * @param {Element} materialItem - The material item DOM element
   * @returns {string|null} The selected format or null
   */
  getSelectedFormat(materialItem) {
    if (!materialItem) return null;
    const selectedBtn = materialItem.querySelector('.format-btn.selected');
    return selectedBtn ? selectedBtn.getAttribute('data-format') : null;
  }

  /**
   * Finds a variant by product ID, format, and purchase type
   * @param {string} productId - Product ID
   * @param {string} selectedFormat - Selected format (digital/print)
   * @param {string} purchaseType - Purchase type (buy/rent/etc)
   * @returns {any|null} The variant or null if not found
   */
  findVariant(productId, selectedFormat, purchaseType) {
    if (!this.productHandler) return null;
    return this.productHandler.findVariant(
      productId,
      selectedFormat,
      purchaseType
    );
  }

  /**
   * Clears all selected products
   * @returns {void}
   */
  clearSelections() {
    this.selectedProducts = [];

    // Uncheck all checkboxes
    const checkboxes = document.querySelectorAll('.purchase-checkbox:checked');
    checkboxes.forEach(
      /** @param {any} checkbox */ checkbox => {
        checkbox.checked = false;
      }
    );

    // Also clear React component selections (suggested materials)
    if (typeof window !== 'undefined' && window.selectedSuggestedMaterials) {
      window.selectedSuggestedMaterials = [];
      // Notify listeners of the change
      if (window.selectedSuggestedMaterialsListeners) {
        window.selectedSuggestedMaterialsListeners.forEach(listener => {
          try {
            listener();
          } catch (e) {
            console.error('Error in selection change listener:', e);
          }
        });
      }
    }

    this.updateSelectionBarSummary();
    this.dispatchSelectionChangeEvent();
  }

  /**
   * Gets the count of selected products
   * @returns {number} Number of selected products
   */
  getSelectedCount() {
    return this.selectedProducts.length;
  }

  /**
   * Gets the total price of selected products
   * @returns {number} Total price
   */
  getTotalPrice() {
    return this.selectedProducts.reduce(
      (sum, /** @param {any} item */ item) => sum + item.price,
      0
    );
  }

  /**
   * Gets the current selected products array
   * @returns {Array<any>} Array of selected products
   */
  getSelectedProducts() {
    return [...this.selectedProducts]; // Return a copy to prevent mutation
  }

  /**
   * Adds selected products to cart
   * @returns {Promise<void>}
   */
  async addSelectedProductsToCart() {
    if (
      this.productHandler &&
      typeof this.productHandler.addSelectedProductsToCart === 'function'
    ) {
      return await this.productHandler.addSelectedProductsToCart();
    }

    // Fallback implementation
    console.warn(
      'ProductHandler not available, using fallback cart implementation'
    );
    this.fallbackAddToCart();
  }

  /**
   * Fallback implementation for adding to cart
   * @private
   * @returns {void}
   */
  fallbackAddToCart() {
    if (this.selectedProducts.length === 0) {
      alert('Please select some products first.');
      return;
    }


    // You could implement Shopify Cart API calls here
    // For now, just show a success message
    alert(`Added ${this.selectedProducts.length} item(s) to cart!`);

    // Clear selections after adding to cart
    this.clearSelections();
  }

  /**
   * Dispatches a custom event when selections change
   * @private
   * @returns {void}
   */
  dispatchSelectionChangeEvent() {
    const event = new CustomEvent('selection:changed', {
      detail: {
        selectedProducts: this.getSelectedProducts(),
        totalCount: this.getSelectedCount(),
        totalPrice: this.getTotalPrice(),
      },
    });
    document.dispatchEvent(event);
  }

  /**
   * Updates both selected products and selection bar summary
   * Convenience method that combines both operations
   * @returns {void}
   */
  refreshSelections() {
    this.updateSelectedProducts();
    this.syncSuggestedMaterials();
    this.updateSelectionBarSummary();
  }

  /**
   * Syncs selected variants from window.selectedSuggestedMaterials to SelectionManager
   * This bridges the React component selections with the SelectionManager
   * @returns {void}
   */
  syncSuggestedMaterials() {
    // Prevent re-entrant calls
    if (this._isSyncing) {
      return;
    }

    if (typeof window === 'undefined' || !window.selectedSuggestedMaterials) {
      return;
    }

    this._isSyncing = true;

    try {
      const suggestedVariants = window.selectedSuggestedMaterials || [];
    
      // Try to get products from multiple sources
      const allProducts = this.products && this.products.length > 0 
        ? this.products 
        : (window.courseData?.products_Materials || []);
      
      if (allProducts.length === 0) {
        console.warn('[SelectionManager] No products available for syncing suggested materials');
        return;
      }
    
      // Extract variant IDs that are already in selectedProducts from suggested materials
      const existingSuggestedIds = new Set(
        this.selectedProducts
          .filter(item => item.source === 'suggested')
          .map(item => this.extractVariantId(item.variant?.id))
      );

      // Add new suggested variants that aren't already in selectedProducts
      suggestedVariants.forEach(variant => {
      const variantId = this.extractVariantId(variant.id);
      
      // Skip if already exists
      if (existingSuggestedIds.has(variantId)) {
        return;
      }

      // Find the parent product for this variant
      // Try multiple ways to find the product
      let product = null;
      
      if (variant.parentProductId) {
        const parentProductId = this.extractProductId(variant.parentProductId);
        product = allProducts.find(p => {
          const pId = String(p.id || '');
          return pId === String(parentProductId) || pId === parentProductId;
        });
      }

      if (product && variant) {
        // Determine purchase type from variant title or options
        const purchaseType = variant.option2 || 
                           (variant.title && variant.title.includes('Rent') ? 'Rent' : 'Buy') ||
                           'Buy';

        // Determine format (Digital/Print)
        const selectedFormat = variant.option1 === 'Digital' || variant.option1 === 'Print' 
          ? variant.option1.toLowerCase() 
          : 'simple';

        const selectedItem = {
          productId: product.id,
          productTitle: product.title || 'Unknown Product',
          selectedFormat: selectedFormat,
          purchaseType: purchaseType,
          variant: variant,
          price: parseFloat(variant.price) || 0,
          source: 'suggested', // Mark as coming from suggested materials component
        };
        
        this.selectedProducts.push(selectedItem);
        existingSuggestedIds.add(variantId);
      } else {
        console.warn('[SelectionManager] Could not find product for variant:', {
          variantId,
          variant,
          parentProductId: variant.parentProductId
        });
      }
    });

      // Remove suggested variants that are no longer in window.selectedSuggestedMaterials
      const currentSuggestedIds = new Set(
        suggestedVariants.map(v => this.extractVariantId(v.id))
      );
      
      const beforeCount = this.selectedProducts.length;
      this.selectedProducts = this.selectedProducts.filter(item => {
        // Keep items that aren't from suggested materials
        if (item.source !== 'suggested') {
          return true;
        }
        // Keep suggested items that are still in the array
        const variantId = this.extractVariantId(item.variant?.id);
        return currentSuggestedIds.has(variantId);
      });
      
      const afterCount = this.selectedProducts.length;
      const addedItems = suggestedVariants.length - existingSuggestedIds.size;
      const hasChanges = beforeCount !== afterCount || addedItems > 0;

      // Only dispatch event if there were actual changes
      if (hasChanges) {
        this.dispatchSelectionChangeEvent();
      }
    } finally {
      this._isSyncing = false;
    }
  }

  /**
   * Extract numeric variant ID from Shopify GID
   * @private
   * @param {string} gid - Shopify GID
   * @returns {string} Numeric ID
   */
  extractVariantId(gid) {
    if (!gid) return null;
    if (typeof gid !== 'string') return String(gid);
    const parts = gid.split('/');
    return parts[parts.length - 1];
  }

  /**
   * Extract numeric product ID from Shopify GID
   * @private
   * @param {string} gid - Shopify GID
   * @returns {string} Numeric ID
   */
  extractProductId(gid) {
    return this.extractVariantId(gid); // Same format
  }
}

// Export for use in other modules and global scope
window.SelectionManager = SelectionManager;

// Create namespace if it doesn't exist
if (!window.CourseMatSystem) {
  window.CourseMatSystem = {};
}
window.CourseMatSystem.SelectionManager = SelectionManager;
